// @no-vnc-web/rtc 前端 signaling WS 客户端。
//
// 对齐:
//   - signaling/internal/ws(Go 服务端)
//   - agent/internal/signaling/cloud.go(Go agent 侧客户端)
//
// 消息外层:{ type, payload? }
// 类型集合:offer / answer / ice / heartbeat / peer_joined / peer_left / error
//
// 前端作为"offer 发起方":
//   1. 用 token 连 /ws/user?t=TOKEN
//   2. 收 peer_joined 后 createOffer → send offer
//   3. 收到 answer → setRemoteDescription
//   4. 双向 ICE trickle
//
// v1 不做自动重连:一个介入会话 90 秒内的重连意义有限,失败就让用户重开页面。

export type WireType =
  | 'offer'
  | 'answer'
  | 'ice'
  | 'heartbeat'
  | 'peer_joined'
  | 'peer_left'
  | 'error';

export interface Envelope<T = unknown> {
  type: WireType;
  payload?: T;
}

export interface SdpPayload {
  sdp: string;
}

export interface IceCandidatePayload {
  candidate: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  usernameFragment?: string | null;
}

export interface SignalingErrorPayload {
  code: number;
  message: string;
}

export interface SignalingHandlers {
  /** 收到对端 offer(agent 通常作为 answer 侧不会走到这里)。*/
  onOffer?: (sdp: string) => void | Promise<void>;
  /** 收到对端 answer(user 侧 createOffer 后等这个)。*/
  onAnswer?: (sdp: string) => void | Promise<void>;
  /** 收到对端 ICE candidate。*/
  onICE?: (cand: IceCandidatePayload) => void | Promise<void>;
  /** 对端 WS 接入。*/
  onPeerJoined?: () => void;
  /** 对端离开(关连)。*/
  onPeerLeft?: () => void;
  /** 服务端主动错误(随后会关闭)。*/
  onError?: (err: SignalingErrorPayload) => void;
  /** WS open。*/
  onOpen?: () => void;
  /** WS close,传原始关闭码与 reason。*/
  onClose?: (code: number, reason: string) => void;
}

export interface SignalingOptions {
  /** wss://signal.example/ws/user(不含 token)。*/
  url: string;
  /** 用户 token;会拼到 query ?t= 里。*/
  token: string;
  /** 应用层心跳(ms),默认 15000。*/
  heartbeatMs?: number;
  /** 超过多久没有 WS message 视为僵死并强制关闭(ms),默认 60000。*/
  idleTimeoutMs?: number;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private hbTimer: ReturnType<typeof setInterval> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private readonly heartbeatMs: number;
  private readonly idleTimeoutMs: number;

  constructor(
    private readonly opts: SignalingOptions,
    private readonly handlers: SignalingHandlers,
  ) {
    this.heartbeatMs = opts.heartbeatMs ?? 15000;
    this.idleTimeoutMs = opts.idleTimeoutMs ?? 60000;
  }

  /**
   * 打开 WS 并在 open 或错误时 resolve/reject。
   * 后续事件通过 handlers 回调。
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const u = urlWithToken(this.opts.url, this.opts.token);
      let settled = false;

      const ws = new WebSocket(u);
      this.ws = ws;

      ws.addEventListener('open', () => {
        settled = true;
        this.handlers.onOpen?.();
        this.startHeartbeat();
        this.resetIdleTimer();
        resolve();
      });
      ws.addEventListener('message', (ev) => {
        this.resetIdleTimer();
        this.handleMessage(ev.data);
      });
      ws.addEventListener('close', (ev) => {
        this.cleanup();
        this.handlers.onClose?.(ev.code, ev.reason);
        if (!settled) reject(new Error(`signaling: closed before open: ${ev.code} ${ev.reason}`));
      });
      ws.addEventListener('error', () => {
        if (!settled) {
          settled = true;
          reject(new Error('signaling: WebSocket error'));
        }
      });
    });
  }

  /** 发 offer(user 侧常用)。*/
  sendOffer(sdp: string): void {
    this.send({ type: 'offer', payload: { sdp } satisfies SdpPayload });
  }

  /** 发 answer(agent 侧常用,前端一般不用)。*/
  sendAnswer(sdp: string): void {
    this.send({ type: 'answer', payload: { sdp } satisfies SdpPayload });
  }

  /** 发本端 ICE candidate。*/
  sendICE(cand: IceCandidatePayload): void {
    this.send({ type: 'ice', payload: cand });
  }

  /** 手工心跳;一般不用调用,构造时已启动 ticker。*/
  sendHeartbeat(): void {
    this.send({ type: 'heartbeat' });
  }

  /** 主动关闭。幂等。*/
  close(code: number = 1000, reason: string = 'client close'): void {
    if (this.closed) return;
    this.closed = true;
    try {
      this.ws?.close(code, reason);
    } catch {
      /* ignore */
    }
    this.cleanup();
  }

  // ----- internals -----

  private send(env: Envelope): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('signaling: not connected');
    }
    this.ws.send(JSON.stringify(env));
  }

  private async handleMessage(data: unknown): Promise<void> {
    if (typeof data !== 'string') return;
    let env: Envelope;
    try {
      env = JSON.parse(data) as Envelope;
    } catch {
      return;
    }
    switch (env.type) {
      case 'heartbeat':
        return;
      case 'peer_joined':
        this.handlers.onPeerJoined?.();
        return;
      case 'peer_left':
        this.handlers.onPeerLeft?.();
        return;
      case 'offer':
        if (this.handlers.onOffer) await this.handlers.onOffer((env.payload as SdpPayload).sdp);
        return;
      case 'answer':
        if (this.handlers.onAnswer) await this.handlers.onAnswer((env.payload as SdpPayload).sdp);
        return;
      case 'ice':
        if (this.handlers.onICE) await this.handlers.onICE(env.payload as IceCandidatePayload);
        return;
      case 'error':
        this.handlers.onError?.(env.payload as SignalingErrorPayload);
        return;
    }
  }

  private startHeartbeat(): void {
    this.hbTimer = setInterval(() => {
      try {
        this.sendHeartbeat();
      } catch {
        /* connection closed mid-beat; cleanup will handle */
      }
    }, this.heartbeatMs);
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.close(4006, 'idle timeout');
    }, this.idleTimeoutMs);
  }

  private cleanup(): void {
    if (this.hbTimer) {
      clearInterval(this.hbTimer);
      this.hbTimer = null;
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}

// ----- step-up HTTP 客户端 -----

export interface StepUpResult {
  ok: true;
}

export class StepUpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: 'invalid_code' | 'rate_limited' | 'unauthorized' | 'bad_input' | 'server',
    message: string,
  ) {
    super(message);
    this.name = 'StepUpError';
  }
}

/**
 * 调用 signaling 的 POST /api/v1/stepup/totp。
 * 成功 resolve;失败抛 StepUpError,按 HTTP 状态分类 code。
 */
export async function verifyTOTP(baseURL: string, token: string, code: string): Promise<StepUpResult> {
  const res = await fetch(baseURL.replace(/\/$/, '') + '/api/v1/stepup/totp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, code }),
  });
  if (res.ok) return { ok: true };
  const text = await res.text();
  let msg = text;
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) msg = j.error;
  } catch {
    /* ignore */
  }
  const code2 = mapStepUpStatus(res.status);
  throw new StepUpError(res.status, code2, msg);
}

function mapStepUpStatus(status: number): StepUpError['code'] {
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 429) return 'rate_limited';
  if (status === 422) return 'invalid_code';
  if (status === 400) return 'bad_input';
  return 'server';
}

// ----- helpers -----

function urlWithToken(base: string, token: string): string {
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}t=${encodeURIComponent(token)}`;
}
